import {
	Button,
	Content,
	Flex,
	FlexItem,
	Grid,
	GridItem,
	List,
	ListItem,
	Popover,
	PopoverPosition,
	Progress,
	ProgressMeasureLocation,
	ProgressSize,
	Skeleton,
	Split,
	SplitItem,
} from "@patternfly/react-core";
import { type CSSProperties, type FC, useMemo, useState } from "react";
import "./ProductScore.scss";
import { ExclamationTriangleIcon } from "@patternfly/react-icons";
import { useParams } from "react-router";
import {
	ProductDescriptions,
	ProductLegalDetailEnums,
	ProductListingTypes,
	ProductSupportEnums,
	TabNames,
} from "../../../../constants/product";
import type { ProductRouteParams } from "../../../../constants/routerconstants";
import { useProductMetadata } from "../../../../hooks/useProductMetadata";
import { useProductScoreMetadata } from "../../../../hooks/useProductScoreMetadata";
import { useChecklistStatus } from "../../../../queries/productDetails";
import {
	CombinedAttachedProjectsType,
	getAttachedProjectsCombinedType,
} from "../../../../utils/attachedProjectValidator";
import Env from "../../../../utils/env";
import { DeployedAsSetType, getDeployedAsSet } from "../../../../utils/productDeployedAsSet";
import { ProductScoreActionItem } from "./ProductScoreActionItem";

interface ValidationErrorState {
	[key: string]: {
		status: boolean;
		nonRequiredFieldCheck?: boolean;
		route: string | null;
		url?: string;
	};
}

interface ProductScoreProps {
	isPublishable: boolean;
	validationErrorState: ValidationErrorState;
}

const ProductScore: FC<ProductScoreProps> = ({ isPublishable, validationErrorState }) => {
	/**
	 * Full logic and details: https://docs.google.com/spreadsheets/d/1a2O4676MmQF1k0MRVovrUypIlhzVPR9E66IDXwU_4fE/edit?usp=sharing
	 */
	const params = useParams<ProductRouteParams>();
	const [popoverState, setPopoverState] = useState(false);
	const {
		productDetail,
		certificationListState,
		componentMetadata: { hasCertified, isValid },
	} = useProductMetadata();
	const closePopup = () => {
		setPopoverState(false);
	};
	const { graphValues, scoreStatus, productScore, isProductScoreLoading, apiProductScoreItems } =
		useProductScoreMetadata();
	// CHK: You have unsigned legal agreements
	const { data: checklistStatus, isFetching: isChecklistFetching } = useChecklistStatus(params.productId);
	const { legalTermsCheckURL, checklistHasError, isChecklistCompleted } = useMemo(() => {
		let checkListItem = null;
		let hasError = false;
		checklistStatus?.items?.forEach((item) => {
			item?.checklistItems?.forEach((item) => {
				if (item?.name?.indexOf("_legal_terms") !== -1 && item?.status === "NOT_COMPLETED") {
					item?.checklistItemActions?.forEach((checklist) => {
						checkListItem = checklist?.link;
					});
				}
				if (item?.status === "ERROR") {
					hasError = true;
				}
			});
		});
		return {
			legalTermsCheckURL: checkListItem,
			checklistHasError: hasError,
			isChecklistCompleted: checklistStatus?.status === "COMPLETED",
		};
	}, [checklistStatus]);
	//CHK: incomplete required items are filtered
	const incompleteRequiredItems = Object.keys(validationErrorState).filter((key) => validationErrorState[key].status);
	const incompleteSet = new Set(incompleteRequiredItems);
	//CHK: Your product is lacking vital information. logic, shown when user hasn't filled at-least 1 required field
	const hideVitalMessages =
		incompleteSet.has(TabNames.general.name) &&
		incompleteSet.has(TabNames.linkedResources.name) &&
		incompleteSet.has(TabNames.support.name) &&
		incompleteSet.has(TabNames.contacts.name) &&
		incompleteSet.has(TabNames.seo.name) &&
		// Validating non required items
		!productDetail?.logo?.url &&
		!((productDetail?.features?.length ?? 0) > 0) &&
		!productDetail?.quick_start_configuration &&
		!((productDetail?.faqs?.length ?? 0) > 0) &&
		!productDetail?.support?.[ProductSupportEnums.phoneNumber] &&
		!productDetail?.support?.[ProductSupportEnums.emailAddress] &&
		!productDetail?.support?.[ProductSupportEnums.description] &&
		!productDetail?.legal?.[ProductLegalDetailEnums.description] &&
		!productDetail?.legal?.[ProductLegalDetailEnums.privacyPolicyUrl];
	//CHK: You have uncertified components
	const attachedUncertifiedComponents = certificationListState?.filter(
		(item) => item?.certification_status !== "Published",
	);
	const showUncertifiedComponentsMessage = attachedUncertifiedComponents?.length > 0 && !isValid;
	//CHK: You haven’t attached all required components
	//CHK:  You have unwanted components attached
	const projectTypeSet = getDeployedAsSet(certificationListState);
	const combinedAttachedType = getAttachedProjectsCombinedType(certificationListState);
	const showSubComponentsValidation =
		certificationListState.length > 0 &&
		(combinedAttachedType === CombinedAttachedProjectsType.operatorOrHelm ||
			combinedAttachedType === CombinedAttachedProjectsType.openShiftCnf);
	const getRequiredComponentValidationReason = (): string => {
		let missingTypes = null;
		switch (combinedAttachedType) {
			case CombinedAttachedProjectsType.operatorOrHelm:
				missingTypes = [projectTypeSet.has(DeployedAsSetType.containers)].some((bool) => bool)
					? null
					: "container image component";
				break;
			case CombinedAttachedProjectsType.openShiftCnf:
				if (hasCertified) {
					// certified
					// Helm chart / Operator validation
					const components = [];
					if (!(projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator))) {
						components.push("helm chart or operator component");
					}
					// Helm chart / Operator validation
					if (!projectTypeSet.has(DeployedAsSetType.containers)) {
						components.push("container image component");
					}
					missingTypes = components.join(" and ");
				} else {
					// validated cnf
					// can have operator or helm attached
					// ref: src/components/ProductComponents/ProductComponents.tsx
					if (projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator)) {
						if (!projectTypeSet.has(DeployedAsSetType.containers)) {
							missingTypes = "container image component";
						}
					}
				}
				break;
			default:
				break;
		}
		return missingTypes ? `You are missing ${missingTypes}` : "";
	};
	const validationReasonRequiredComponents = getRequiredComponentValidationReason() || "";
	const getNotRequiredComponentValidationReason = (): string => {
		let message = "";
		if (combinedAttachedType === CombinedAttachedProjectsType.openShiftCnf && !hasCertified) {
			// validated cnf
			if (
				!(projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator)) &&
				projectTypeSet.has(DeployedAsSetType.containers)
			)
				message = "Remove incompatible components in order to publish your product.";
		}
		if (
			(projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator)) &&
			(projectTypeSet.has(DeployedAsSetType.rhel) || projectTypeSet.has(DeployedAsSetType.openstackAppContainer))
		) {
			message = "Remove incompatible components in order to publish your product.";
		}
		return message;
	};
	const validationReasonNonRequiredComponents = getNotRequiredComponentValidationReason() || "";
	//CHK: Your company profile is incomplete
	const showCompanyDetailsValidation = incompleteSet.has("Company Details");
	// CHK: Problem retrieving checklist
	// for checklist errors
	let checklistErrorMessage = null;
	if (checklistHasError) {
		checklistErrorMessage = (
			<ProductScoreActionItem
				title={"Unable to load publishing requirements"}
				description={"Refresh the page to try again. If the issue persists, please contact support."}
				buttonText={"Contact support"}
				buttonType="external"
				buttonPath={`${Env.ConnectPortalUrls[Env.getEnvName()]}support/technology-partner/#/`}
			/>
		);
	}
	// CHK: You have not certified or validated your product // For containers and traditional apps
	const showContainersOrStandaloneMessage =
		(productDetail?.type === ProductListingTypes.ContainerStack ||
			productDetail?.type === ProductListingTypes.TraditionalApplication) &&
		certificationListState?.length === 0 &&
		productDetail?.product_validation?.partner_validated !== true;
	// CHK: You have not certified your product // For OpenStack Infrastructure apps
	const showOSInfraMessage =
		productDetail?.type === ProductListingTypes.OpenStackInfrastructure && certificationListState?.length === 0;

	// CHK: Checklist incomplete even though all the local checks passed
	let inconsistentChecklistValidation = null;
	if (
		!isPublishable &&
		!checklistHasError &&
		!hideVitalMessages &&
		!showUncertifiedComponentsMessage &&
		!(showSubComponentsValidation && (validationReasonRequiredComponents || validationReasonNonRequiredComponents)) &&
		!showCompanyDetailsValidation &&
		!legalTermsCheckURL &&
		!(showOSInfraMessage || showOSInfraMessage) &&
		incompleteSet?.size === 0 &&
		!isChecklistCompleted
	) {
		inconsistentChecklistValidation = (
			<ProductScoreActionItem
				title={"Checklist is not completed"}
				description={"There was an issue with validating the checklist. Please reach out to support for assistance."}
				buttonText={"Contact support"}
				buttonType="external"
				buttonPath={`${Env.ConnectPortalUrls[Env.getEnvName()]}support/technology-partner/#/`}
			/>
		);
	}
	if (isChecklistFetching || isProductScoreLoading) {
		return (
			<Grid hasGutter={true} className="header-product-score">
				<GridItem span={12}>
					<Flex fullWidth={{ default: "fullWidth" }} spaceItems={{ default: "spaceItemsNone" }}>
						<FlexItem
							grow={{
								default: "grow",
							}}
						>
							<Skeleton />
						</FlexItem>
					</Flex>
					<Flex fullWidth={{ default: "fullWidth" }} spaceItems={{ default: "spaceItemsNone" }}>
						<FlexItem
							grow={{
								default: "grow",
							}}
						>
							<Skeleton />
						</FlexItem>
					</Flex>
				</GridItem>
			</Grid>
		);
	}
	return (
		<Grid hasGutter={false} className="header-product-score">
			<GridItem offset={3} span={9}>
				<Flex fullWidth={{ default: "fullWidth" }} spaceItems={{ default: "spaceItemsNone" }}>
					<FlexItem aria-hidden={true}>
						{isPublishable ? (
							<Content>
								<Content component="p" className="small-text">
									{scoreStatus}
								</Content>
							</Content>
						) : (
							<Content>
								<Content component="p" className="small-text-warning">
									<ExclamationTriangleIcon color="#f1ac01" /> Not ready to publish
								</Content>
							</Content>
						)}
					</FlexItem>
					<span className="pf-v6-u-screen-reader">
						{`Product score is ${productScore} out of 29. ${isPublishable ? `Its status is ${scoreStatus}` : "Product is not ready to be published."}`}
					</span>
					<FlexItem align={{ default: "alignRight" }}>
						{!isPublishable && (
							<Popover
								minWidth={"25rem"}
								className={"product-score-popup"}
								headerContent={"Why can't I publish?"}
								isVisible={popoverState}
								zIndex={9999}
								enableFlip={true}
								showClose={true}
								shouldClose={closePopup}
								position={PopoverPosition.bottom}
								bodyContent={
									<>
										<span>
											The following issues prevent your product from being published. To resolve all issues and publish
											your product, follow the instructions to resolve an issue.
										</span>
										<div>
											{/*Your product is lacking vital information.*/}
											{hideVitalMessages ? (
												<ProductScoreActionItem
													title={"Your product is lacking vital information"}
													description={"Your product must have basic marketing " + "information to be published."}
													buttonText={"Manage product information"}
													buttonType="link"
													buttonPath={TabNames.general.route}
												/>
											) : (
												<>
													{/*Displayed only after at least 1 tab is filled in the product details*/}
													{/*Your product is missing general product information*/}
													{incompleteSet.has(TabNames.general.name) && (
														<ProductScoreActionItem
															title={"Your product is missing general product information"}
															description={"You have not filled out the following required fields:"}
															subItem={
																<List className={"popup-list"}>
																	{!productDetail?.name && <ListItem>Product Name</ListItem>}
																	{!productDetail?.descriptions?.[ProductDescriptions.short] && (
																		<ListItem>Brief description</ListItem>
																	)}
																	{!productDetail?.descriptions?.[ProductDescriptions.long] && (
																		<ListItem>Long description</ListItem>
																	)}
																</List>
															}
															buttonText={"Manage general product information"}
															buttonType="link"
															buttonPath={TabNames.general.route}
														/>
													)}
													{/*Your product is missing linked resources*/}
													{incompleteSet.has(TabNames.linkedResources.name) && (
														<ProductScoreActionItem
															title={"Your product is missing linked resources"}
															description={`You must include at least one required linked resource before publishing`}
															buttonText={"Manage linked resources"}
															buttonType="link"
															buttonPath={TabNames.linkedResources.route}
														/>
													)}
													{/*Your product is missing support information*/}
													{incompleteSet.has(TabNames.support.name) && (
														<ProductScoreActionItem
															title={"Your product is missing support information"}
															description={"You must enter a support website to publish."}
															buttonText={"Manage support information"}
															buttonType="link"
															buttonPath={TabNames.support.route}
														/>
													)}
													{/*Your product is missing SEO information*/}
													{incompleteSet.has(TabNames.seo.name) && (
														<ProductScoreActionItem
															title={"Your product is missing SEO information"}
															description={`You must set a product category to publish.`}
															buttonText={"Manage SEO information"}
															buttonType="link"
															buttonPath={TabNames.seo.route}
														/>
													)}
													{/*Your product is missing contacts*/}
													{incompleteSet.has(TabNames.contacts.name) && (
														<ProductScoreActionItem
															title={"Your product is missing contacts"}
															description={"You must add a marketing and technical contact to publish."}
															buttonText={"Manage contacts"}
															buttonType="link"
															buttonPath={TabNames.contacts.route}
														/>
													)}
												</>
											)}
											{/*You have not attached any components to this product*/}
											{showContainersOrStandaloneMessage && (
												<ProductScoreActionItem
													title={"You have not certified or validated your product"}
													description={"Please follow the procedures for validation or certification."}
													buttonText={"Validations & certifications"}
													buttonType="link"
													buttonPath={"validations-and-certifications"}
												/>
											)}
											{/*You have not attached any components to this product*/}
											{showOSInfraMessage && (
												<ProductScoreActionItem
													title={"You have not attached any components to this product"}
													description={"You must attach at least one component to publish a product."}
													buttonText={"Manage components"}
													buttonType="link"
													buttonPath={"components"}
												/>
											)}
											{/*You have uncertified components*/}
											{showUncertifiedComponentsMessage && (
												<ProductScoreActionItem
													title={"You have uncertified components"}
													buttonText={"Manage components"}
													buttonType="link"
													buttonPath={"components"}
													description={
														"The following attached components are uncertified. Ensure that all components attached to your product are certified."
													}
													subItem={
														<List className={"popup-list"}>
															{attachedUncertifiedComponents.map((project, index) => (
																<ListItem key={index}>
																	<Button
																		isInline={true}
																		variant={"link"}
																		component={"a"}
																		href={`${Env.projectPrefix}/${productDetail?._id}/${project?._id}`}
																	>
																		{project?.name}
																	</Button>
																</ListItem>
															))}
														</List>
													}
												/>
											)}
											{/*You haven’t attached all required components*/}
											{showSubComponentsValidation && validationReasonRequiredComponents && (
												<ProductScoreActionItem
													title={"You haven’t attached all required components"}
													buttonText={"Manage components"}
													buttonType="link"
													buttonPath={"components"}
													description={validationReasonRequiredComponents}
												/>
											)}
											{/*You have unwanted components attached*/}
											{showSubComponentsValidation && validationReasonNonRequiredComponents && (
												<ProductScoreActionItem
													title={"You have attached incompatible components"}
													buttonText={"Manage components"}
													buttonType="link"
													buttonPath={"components"}
													description={validationReasonNonRequiredComponents}
												/>
											)}
											{/*Your company profile is incomplete*/}
											{showCompanyDetailsValidation && (
												<ProductScoreActionItem
													title={"Your company profile is incomplete"}
													description={"You must complete your company profile to publish a product."}
													buttonText={"Fill out company profile"}
													buttonType="external"
													buttonPath={validationErrorState?.["Company Details"]?.url}
												/>
											)}
											{/*You have unsigned legal agreements.*/}
											{legalTermsCheckURL && (
												<ProductScoreActionItem
													title={"You have unsigned legal agreements"}
													description={"You must review and sign the required legal agreements to publish."}
													buttonText={"View legal agreements"}
													buttonType="external"
													buttonPath={legalTermsCheckURL}
												/>
											)}
											{checklistErrorMessage}
											{inconsistentChecklistValidation}
										</div>
									</>
								}
							>
								<Button
									variant="link"
									isInline={true}
									id={"popover-trigger"}
									className="why-cant-publish"
									onClick={() => {
										setPopoverState((prev) => !prev);
									}}
								>
									Why can't I publish?
								</Button>
							</Popover>
						)}
						{isPublishable && apiProductScoreItems.length > 0 && (
							<Popover
								minWidth={"28rem"}
								hideOnOutsideClick={true}
								className={"product-score-popup"}
								headerContent={"How do I improve my score?"}
								isVisible={popoverState}
								zIndex={9999}
								enableFlip={true}
								showClose={true}
								shouldClose={closePopup}
								position={PopoverPosition.left}
								bodyContent={
									<div className={"body"}>
										<span>Doing any of the following will improve your product score.</span>
										<div>
											{apiProductScoreItems.map((item, index) => (
												<ProductScoreActionItem
													key={index}
													title={item.title}
													htmlDescription={item.description}
													buttonText={item.buttonText}
													buttonType={item.buttonType}
													buttonPath={item.buttonPath}
												/>
											))}
										</div>
									</div>
								}
							>
								<Button
									variant="link"
									isInline={true}
									id={"popover-trigger"}
									className={"why-cant-publish"}
									onClick={() => {
										setPopoverState((prev) => !prev);
									}}
								>
									How do I improve my score?
								</Button>
							</Popover>
						)}
					</FlexItem>
				</Flex>
			</GridItem>
			<GridItem aria-hidden={true} span={3}>
				<Flex>
					<FlexItem align={{ default: "alignRight" }}>
						<Content>
							<Content component="p" className="header-product-score__text">
								Product score
							</Content>
						</Content>
					</FlexItem>
				</Flex>
			</GridItem>
			<GridItem aria-hidden={true} offset={3} span={9}>
				<Split hasGutter={false}>
					{graphValues?.map((graph, index) => (
						<SplitItem
							key={`graph-${index}`}
							style={{ ...(graph?.minWidth ? { minWidth: graph.minWidth } : {}) }}
							isFilled={!graph?.minWidth}
						>
							<Progress
								aria-label={"main"}
								className="header-product-score__graph"
								style={
									{
										"--pf-v6-c-progress__indicator--BackgroundColor": graph?.color,
										"--pf-v6-c-progress__bar--BackgroundColor": `${graph?.color}33`,
									} as CSSProperties
								}
								value={graph?.value || 0}
								size={ProgressSize.sm}
								measureLocation={ProgressMeasureLocation.none}
							/>
						</SplitItem>
					))}
				</Split>
			</GridItem>
		</Grid>
	);
};

export default ProductScore;
